// Order-scoped patient H5 branding. No patient data or credentials are persisted.
export function parseThemeColor (extendInfo) {
  try {
    const info = typeof extendInfo === 'string' ? JSON.parse(extendInfo) : extendInfo
    const value = info && info.themeColor
    return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim().toLowerCase() : null
  } catch (_) { return null }
}

function luminance (hex) {
  const rgb = hex.match(/[0-9a-f]{2}/gi).map(value => {
    const n = parseInt(value, 16) / 255
    return n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4)
  })
  return rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722
}
function mix (color, target, ratio) {
  return '#' + color.slice(1).match(/../g).map((v, i) => Math.round(parseInt(v, 16) * (1 - ratio) + parseInt(target.slice(1).substr(i * 2, 2), 16) * ratio).toString(16).padStart(2, '0')).join('')
}
export function themeTokens (color) {
  const light = luminance(color)
  const channels = color.slice(1).match(/../g).map(value => parseInt(value, 16))
  const lightness = (Math.max(...channels) + Math.min(...channels)) / 510
  // Preserve the existing white labels/checkmarks on ordinary brand colors.
  // Dark foregrounds are only a fallback for pale or very bright backgrounds.
  const useDarkForeground = light >= 0.6 || lightness >= 0.72
  let text = color
  while (luminance(text) > 0.16) text = mix(text, '#000000', 0.12)
  return {
    '--order-primary': color,
    '--order-icon-filter': useDarkForeground ? 'brightness(0)' : 'brightness(0) invert(1)',
    '--order-on-primary': useDarkForeground ? '#000000' : '#ffffff',
    '--order-text': text,
    '--order-soft': mix(color, '#ffffff', 0.9),
    '--order-border': mix(text, '#ffffff', 0.45)
  }
}
export function orderThemeKey (params = {}) {
  const order = params.orderID || params.inquiryOrderID
  const client = params.clientID || params.clientAppID || ''
  return order ? JSON.stringify([client, 'order', String(order)]) : params.bizID ? JSON.stringify([client, 'biz', String(params.bizID)]) : ''
}

const prefix = 'patient-order-theme:v1:'
let activeKey = ''
let activeColor = null
let legacyPrimary = ''
let generation = 0
let requestSequence = 0
function read (key) { try { return sessionStorage.getItem(prefix + key) } catch (_) { return null } }
function write (key, value) { try { sessionStorage.setItem(prefix + key, value) } catch (_) { /* storage can be disabled */ } }
function render (color) {
  activeColor = color
  const root = document.documentElement
  Object.keys(themeTokens('#000000')).forEach(key => root.style.removeProperty(key))
  if (color) {
    Object.entries(themeTokens(color)).forEach(([key, value]) => root.style.setProperty(key, value))
    root.style.setProperty('--primary-color', color)
    root.setAttribute('data-order-theme', '')
  } else {
    root.removeAttribute('data-order-theme')
    if (legacyPrimary) root.style.setProperty('--primary-color', legacyPrimary)
    else root.style.removeProperty('--primary-color')
  }
}
export function setLegacyTheme (color) {
  legacyPrimary = color || ''
  if (!activeColor) render(null)
}
function activate (key) {
  if (key !== activeKey) { generation++; activeKey = key; legacyPrimary = '' }
  write('active', key)
  render(key ? parseThemeColor({ themeColor: read(key) }) : null)
}
export function installOrderTheme (router, http, getContext) {
  const initialQuery = Object.fromEntries(new URLSearchParams(location.search))
  activate(orderThemeKey(initialQuery) || (location.pathname === '/' ? '' : read('active') || ''))
  router.beforeEach((to, from, next) => {
    const key = orderThemeKey(to.query || {})
    if (key && key !== activeKey) activate(key)
    else if (to.path === '/' && !key) activate('')
    next()
  })
  http.interceptors.request.use(config => {
    if (!/\/(?:openRemoteInquiry\/order|commonRp\/h5inquiry)\/getCache(?:\?|$)/.test(config.url || '')) return config
    let body = config.data || {}
    if (typeof body === 'string') { try { body = JSON.parse(body) } catch (_) { body = {} } }
    const key = orderThemeKey({ ...body, ...config.params, ...getContext() })
    if (key && key !== activeKey) activate(key)
    config.orderThemeContext = { key: activeKey, generation, sequence: ++requestSequence }
    return config
  })
  // Registered after the application's normalizer: only successful responses reach here.
  // The response context is carried through by the existing response interceptor.
  http.interceptors.response.use(result => {
    const context = result.orderThemeContext
    if (context && context.key && context.key === activeKey && context.generation === generation && context.sequence === requestSequence) {
      const color = parseThemeColor(result.data && result.data.extendInfo)
      write(activeKey, color || '')
      render(color)
    }
    return result
  })
}
